import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import cashDrawerService from '../../services/cashDrawerService';

const LowCashAlert = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchAlerts();
    // Refresh alerts every 5 minutes
    const interval = setInterval(fetchAlerts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await cashDrawerService.getAlerts();
      if (response.success && response.data) {
        setAlerts(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch cash drawer alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const handleDismiss = () => {
    setDismissed(true);
  };

  const handleNavigateToDrawer = (drawerUuid) => {
    navigate(`/cash-drawers?drawer=${drawerUuid}`);
  };

  // Don't render if loading, dismissed, or no alerts
  if (loading || dismissed || alerts.length === 0) {
    return null;
  }

  return (
    <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg p-4 mb-6 shadow-sm">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" aria-hidden="true" />
        </div>
        <div className="ml-3 flex-1 rtl:ml-0 rtl:mr-3">
          <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-200">
            {t('cashDrawers.lowBalanceAlerts', 'Low Balance Alerts')}
          </h3>
          <div className="mt-2 text-sm text-amber-700 dark:text-amber-300">
            <ul className="list-disc pl-5 rtl:pr-5 rtl:pl-0 space-y-1">
              {alerts.map((alert, index) => (
                <li key={`${alert.drawerUuid}-${alert.currencyCode}-${index}`}>
                  <button
                    onClick={() => handleNavigateToDrawer(alert.drawerUuid)}
                    className="hover:underline focus:outline-none focus:underline text-left rtl:text-right"
                  >
                    <span className="font-medium">{alert.drawerName}</span>
                    {' - '}
                    <span className="font-semibold">{alert.currencyCode}</span>
                    {': '}
                    {formatCurrency(alert.balance)}
                    {' '}
                    <span className="text-amber-600 dark:text-amber-400">
                      ({t('cashDrawers.threshold', 'Threshold')}: {formatCurrency(alert.threshold)})
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-3">
            <button
              onClick={() => navigate('/cash-drawers')}
              className="text-sm font-medium text-amber-800 dark:text-amber-200 hover:text-amber-900 dark:hover:text-amber-100 underline"
            >
              {t('cashDrawers.title', 'Cash Drawers')} &rarr;
            </button>
          </div>
        </div>
        <div className="ml-auto pl-3 rtl:ml-0 rtl:pl-0 rtl:pr-3 rtl:mr-auto">
          <button
            onClick={handleDismiss}
            className="inline-flex rounded-md p-1.5 text-amber-500 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-800/50 focus:outline-none focus:ring-2 focus:ring-amber-600 dark:focus:ring-amber-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
          >
            <span className="sr-only">{t('common.close', 'Close')}</span>
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LowCashAlert;
